import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Camera, Plus, Trash2, MoreHorizontal, ChevronDown, Check, Loader2 } from "lucide-react";

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div className="grid grid-cols-[1fr,1.4fr] items-center gap-3 py-2.5">
      <label className="text-white/90 text-[15px] font-medium">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-10 px-4 rounded-full bg-transparent border border-white/40 text-white text-sm text-center placeholder-white/40 focus:outline-none focus:border-[#14c2a1] focus:ring-2 focus:ring-[#14c2a1]/25 transition-all"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="grid grid-cols-[1fr,1.4fr] items-center gap-3 py-2.5 relative">
      <label className="text-white/90 text-[15px] font-medium">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="h-10 px-4 rounded-full bg-transparent border border-white/40 text-white text-sm flex items-center justify-center gap-2 hover:border-white/60 transition-colors"
      >
        {value || "—"}
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-14 right-0 w-[60%] z-20 rounded-xl border border-white/10 bg-[#2b3134]/95 backdrop-blur-md shadow-xl overflow-hidden">
          {options.map((o) => (
            <button
              key={o}
              onClick={() => {
                onChange(o);
                setOpen(false);
              }}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                value === o ? "bg-[#00a88a]/20 text-white" : "text-white/80 hover:bg-white/5"
              }`}
            >
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Profilo() {
  const { user, updateUser, removeMattress, api } = useAuth();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [gender, setGender] = useState(user?.gender || "MASCHIO");
  const [birthDate, setBirthDate] = useState(user?.birthDate || "");
  const [height, setHeight] = useState(user?.height || "");
  const [weight, setWeight] = useState(user?.weight || "");
  const [avatar, setAvatar] = useState(user?.avatar || null);
  const [toast, setToast] = useState("");
  const [savingBody, setSavingBody] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 1800);
  };

  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = URL.createObjectURL(f);
    setAvatar(url);
    updateUser({ avatar: url });
    showToast("Immagine aggiornata");
  };

  const saveField = (patch) => updateUser(patch);

  const onSaveBodyMetrics = async () => {
    if (savingBody) return;
    const body = {};
    const h = String(height).trim();
    const w = String(weight).trim();
    if (h !== "") {
      const hn = parseInt(h, 10);
      if (Number.isNaN(hn) || hn < 50 || hn > 250) {
        showToast("Altezza non valida (50-250 cm)");
        return;
      }
      body.height_cm = hn;
    }
    if (w !== "") {
      const wn = parseFloat(w.replace(",", "."));
      if (Number.isNaN(wn) || wn < 20 || wn > 300) {
        showToast("Peso non valido (20-300 kg)");
        return;
      }
      body.weight_kg = wn;
    }
    if (Object.keys(body).length === 0) {
      showToast("Inserisci altezza o peso");
      return;
    }
    try {
      setSavingBody(true);
      await api.patch("/api/auth/me/preferences", body);
      updateUser({ height, weight });
      showToast("Salvato");
    } catch (e) {
      const msg =
        e?.response?.data?.detail && typeof e.response.data.detail === "string"
          ? e.response.data.detail
          : "Errore di salvataggio";
      showToast(msg);
    } finally {
      setSavingBody(false);
    }
  };

  const mattresses = user?.mattresses || [];

  return (
    <div className="pb-36">
      {/* Header cover */}
      <div className="relative">
        <div className="relative h-[210px] overflow-hidden">
          <img
            src={
              avatar ||
              "https://images.unsplash.com/photo-1540518614846-7eded433c457?w=1000&auto=format&fit=crop"
            }
            alt="cover"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#2b3134]" />
        </div>

        <label className="absolute -bottom-8 left-5 cursor-pointer group">
          <div className="w-[88px] h-[88px] rounded-full bg-white shadow-xl flex items-center justify-center ring-4 ring-[#2b3134] transition-transform group-hover:scale-[1.03]">
            {avatar ? (
              <img
                src={avatar}
                alt="avatar"
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <Camera size={32} className="text-[#0f3b4d]" strokeWidth={2} />
            )}
          </div>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onPickImage}
          />
        </label>
      </div>

      <div className="px-5 pt-12">
        <h2 className="text-white text-lg font-medium">Modifica immagine</h2>
      </div>

      <div className="mx-5 mt-4 h-px bg-[#14c2a1]/60" />

      <section className="px-5 pt-4">
        <h3 className="text-white text-[22px] font-semibold tracking-tight">
          Informazioni sull&apos;utente
        </h3>

        <div className="mt-3">
          <Field
            label="Nome"
            value={firstName}
            onChange={(v) => {
              setFirstName(v);
              saveField({ firstName: v });
            }}
            placeholder="Nome"
          />
          <Field
            label="Cognome"
            value={lastName}
            onChange={(v) => {
              setLastName(v);
              saveField({ lastName: v });
            }}
            placeholder="Cognome"
          />
          <SelectField
            label="Genere"
            value={gender}
            onChange={(v) => {
              setGender(v);
              saveField({ gender: v });
            }}
            options={["MASCHIO", "FEMMINA", "ALTRO"]}
          />
          <Field
            label="Data di nascita"
            value={birthDate}
            onChange={(v) => {
              setBirthDate(v);
              saveField({ birthDate: v });
            }}
            placeholder="gg/mm/aaaa"
          />
          <Field
            label="Altezza"
            value={height}
            onChange={(v) => setHeight(v)}
            placeholder="cm"
            type="number"
          />
          <Field
            label="Peso"
            value={weight}
            onChange={(v) => setWeight(v)}
            placeholder="kg"
            type="number"
          />

          <div className="pt-3 flex justify-end">
            <button
              type="button"
              onClick={onSaveBodyMetrics}
              disabled={savingBody}
              data-testid="profile-save-body-metrics-btn"
              className="h-10 px-5 rounded-full bg-[#14c2a1] text-[#0b3a34] text-sm font-semibold flex items-center gap-2 shadow-[0_6px_18px_-6px_rgba(20,194,161,0.6)] hover:bg-[#11ad8f] active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {savingBody ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Salvataggio…
                </>
              ) : (
                <>
                  <Check size={15} strokeWidth={3} />
                  Salva
                </>
              )}
            </button>
          </div>
        </div>
      </section>

      <div className="mx-5 mt-5 h-px bg-[#14c2a1]/60" />

      <section className="px-5 pt-4">
        <h3 className="text-white text-[22px] font-semibold tracking-tight">
          I miei materassi
        </h3>

        <div className="mt-3 space-y-3">
          {mattresses.length === 0 && (
            <p className="text-white/60 text-sm">
              Nessun materasso collegato. Aggiungi il tuo primo SmartDream.
            </p>
          )}
          {mattresses.map((m) => (
            <div
              key={m.id}
              className="grid grid-cols-[1fr,auto] items-center gap-3"
            >
              <div className="text-white text-[15px] leading-snug pr-2">
                {m.label}
              </div>
              <div className="flex items-center gap-2">
                <div className="h-9 pl-3 pr-1 rounded-full border border-white/40 text-white text-xs flex items-center gap-2">
                  <span className="font-mono tracking-tight">{m.id}</span>
                  <button
                    onClick={() => setConfirmRemoveId(m.id)}
                    className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center"
                    aria-label="Rimuovi materasso"
                    data-testid={`mattress-remove-btn-${m.id}`}
                  >
                    <MoreHorizontal size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={() => navigate("/onboarding/add-mattress")}
          className="mt-5 w-full h-12 rounded-2xl border border-[#14c2a1] text-white text-[15px] font-medium flex items-center justify-center gap-2 hover:bg-[#14c2a1]/10 transition-colors"
        >
          <span className="w-6 h-6 rounded-full bg-[#14c2a1] flex items-center justify-center">
            <Plus size={14} className="text-[#0b3a34]" strokeWidth={3} />
          </span>
          Aggiungi materasso
        </button>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <button
            onClick={() => {
              updateUser({ mattresses: [] });
              showToast("Dati materassi eliminati");
            }}
            className="h-11 rounded-xl border border-amber-400/80 text-amber-300 text-[13px] font-medium flex items-center justify-center gap-2 hover:bg-amber-400/10 transition-colors"
          >
            <Trash2 size={14} /> Elimina Dati Materassi
          </button>
          <button
            onClick={() => showToast("Profilo eliminato (demo)")}
            className="h-11 rounded-xl border border-rose-400/80 text-rose-300 text-[13px] font-medium flex items-center justify-center gap-2 hover:bg-rose-400/10 transition-colors"
          >
            <Trash2 size={14} /> Elimina Profilo
          </button>
        </div>
      </section>

      {toast && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-24 z-50 px-4 py-2 rounded-full bg-[#14c2a1] text-[#0b3a34] text-sm font-medium shadow-xl">
          {toast}
        </div>
      )}

      {confirmRemoveId && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          onClick={() => setConfirmRemoveId(null)}
          data-testid="mattress-remove-confirm-modal"
        >
          <div
            className="w-full max-w-[400px] bg-[#2b3134] border border-white/10 rounded-2xl p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start gap-3">
              <span className="w-10 h-10 rounded-full bg-rose-500/15 flex items-center justify-center shrink-0">
                <Trash2 size={18} className="text-rose-400" />
              </span>
              <div className="flex-1">
                <h4 className="text-white text-[17px] font-semibold leading-snug">
                  Rimuovere il materasso?
                </h4>
                <p className="text-white/70 text-sm mt-1.5 leading-relaxed">
                  Sei sicuro di voler rimuovere questo materasso?
                </p>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button
                onClick={() => setConfirmRemoveId(null)}
                data-testid="mattress-remove-cancel-btn"
                className="h-11 rounded-xl border border-white/30 text-white text-sm font-medium hover:bg-white/5 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={() => {
                  const id = confirmRemoveId;
                  setConfirmRemoveId(null);
                  removeMattress(id);
                  showToast("Materasso rimosso");
                }}
                data-testid="mattress-remove-confirm-btn"
                className="h-11 rounded-xl bg-rose-500 text-white text-sm font-semibold hover:bg-rose-600 active:scale-[0.98] transition-all shadow-[0_6px_18px_-6px_rgba(244,63,94,0.6)]"
              >
                Rimuovi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
